import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { KIND } from "baseui/notification";
import { ActionsContainer, DeleteUserButton, DuplicateUserButton } from "./styledComponents";
import { ConfirmDeleteUserModal } from "./ConfirmDeleteUserModal";
import { InternalUser } from "./InternalUser";
import { UserType } from "../../../api/bluecrew/types";
import { showClonedUserModal } from "../slices/manageUsersModalSlice";
import { useDeleteManageUsersSplitIsOn } from "../../../../shared/hooks/split";
import { useAppSelector } from "../../../redux";
import { getDeleteUsersPermission } from "../../../redux/selectors/auth";
import { useDeleteFormerColleague } from "../../../api/bluecrew/hooks/user";
import { logError } from "../../../sentry";
import { LoadingModal } from "../../../components/LoadingModal";
import { showToastAction } from "../../../redux/actions/app";
import messages from "../../../../shared/messages";

enum DeleteState {
  Idle = "idle",
  Confirming = "confirming",
  Deleting = "deleting",
}

type TableActionsProps = {
  user: InternalUser;
};

export const TableActions = ({ user }: TableActionsProps) => {
  const dispatch = useDispatch();
  const isDeleteFlagOn = useDeleteManageUsersSplitIsOn();
  const canDeleteUsers = useAppSelector(getDeleteUsersPermission);
  const canShowDelete =
    isDeleteFlagOn &&
    canDeleteUsers &&
    user.userRole !== UserType.SUPER_ADMIN;

  const [deleteState, setDeleteState] = useState<DeleteState>(DeleteState.Idle);

  const { mutate: deleteUser } = useDeleteFormerColleague({
    onSuccess: () => {
      setDeleteState(DeleteState.Idle);
      dispatch(showToastAction(messages.DELETE_INTERNAL_USER.success, KIND.positive));
    },
    onError: (error) => {
      setDeleteState(DeleteState.Idle);
      logError({ error, context: "deleteFormerColleague" });
      dispatch(showToastAction(error.message, KIND.negative, 6000));
    },
  });

  const handleConfirmDelete = () => {
    setDeleteState(DeleteState.Deleting);
    deleteUser(user.userId);
  };

  return (
    <ActionsContainer>
      {canShowDelete && (
        <DeleteUserButton
          data-testid={`delete-user-button-${user.userId}`}
          aria-label="Delete user"
          title="Delete user"
          className={"action"}
          onClick={() => setDeleteState(DeleteState.Confirming)}
        />
      )}
      {user.userRole !== UserType.SUPER_ADMIN && (
        <DuplicateUserButton
          onClick={() => dispatch(showClonedUserModal(user))}
          className={"action"}
        />
      )}
      {canShowDelete && (
        <ConfirmDeleteUserModal
          name={user.name}
          showModal={deleteState === DeleteState.Confirming}
          toggle={() => setDeleteState(DeleteState.Idle)}
          deleteUser={handleConfirmDelete}
        />
      )}
      {deleteState === DeleteState.Deleting && <LoadingModal message={messages.DELETE_INTERNAL_USER.loading} />}
    </ActionsContainer>
  );
};
